import React from 'react'
import { Link } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import { Helmet } from 'react-helmet-async'
import {
  FaLeaf, FaTruck, FaFlask, FaUsers, FaHeart, FaCheckCircle,
} from 'react-icons/fa'
import ImagePlaceholder from '../components/ImagePlaceholder'

const VALUES = [
  { Icon: FaLeaf,       title: 'Naturalidade',    text: 'Ingredientes 100% naturais, sem conservantes, corantes ou óleos refinados.' },
  { Icon: FaFlask,      title: 'Ciência',          text: 'Curadoria técnica rigorosa com nutricionistas, médicos e educadores físicos.' },
  { Icon: FaHeart,      title: 'Bem-estar',         text: 'Cada refeição contribui para uma vida mais saudável, com energia e equilíbrio.' },
  { Icon: FaUsers,      title: 'Inclusividade',     text: 'Sem glúten e sem lactose — acessível para crianças, adultos e idosos.' },
  { Icon: FaTruck,      title: 'Confiabilidade',    text: 'Frota refrigerada própria garante qualidade da produção até a sua mesa.' },
  { Icon: FaCheckCircle,title: 'Praticidade',       text: 'Refeições completas prontas em apenas 6 minutos, com validade de até 180 dias.' },
]

export default function QuemSomos() {
  return (
    <>
      <Helmet>
        <title>Quem Somos – Clean Foods Brasil</title>
        <meta name="description" content="Conheça a história da Clean Foods Brasil, pioneira em marmitas congeladas saudáveis no interior de São Paulo desde 2015." />
      </Helmet>

      {/* ===== PAGE HERO ===== */}
      <div style={{ paddingTop: 72 }}>
        <section className="page-hero">
          <Container>
            <Row>
              <Col lg={8}>
                <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                  Clean Foods Brasil
                </p>
                <h1 className="section-title mb-0">
                  Quem <span className="highlight">Somos</span>
                </h1>
                <hr className="section-divider" />
                <p style={{ maxWidth: 560, lineHeight: 1.8 }}>
                  Desde 2015, transformando a alimentação saudável em algo prático, saboroso e acessível.
                  Pioneira no interior de São Paulo.
                </p>
              </Col>
            </Row>
          </Container>
        </section>
      </div>

      {/* ===== HISTÓRIA ===== */}
      <section className="section-padding bg-cf-black">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={6}>
              <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                Nossa História
              </p>
              <h2 className="section-title mb-0">
                Uma marca com <span className="highlight">propósito</span>
              </h2>
              <hr className="section-divider" />
              <p className="mb-4" style={{ lineHeight: 1.9 }}>
                Desde 2015, a Clean Foods Brasil nasceu com um propósito claro: transformar a alimentação
                saudável em algo prático, saboroso e acessível. Sendo pioneira no interior de São Paulo,
                nossa marca surgiu para facilitar a rotina de quem busca performance e bem-estar, sem
                abrir mão do prazer de comer bem.
              </p>
              <p className="mb-4" style={{ lineHeight: 1.9 }}>
                Mais do que apenas marmitas congeladas, entregamos um conceito de nutrição inteligente.
                Cada receita é desenvolvida através de curadoria técnica rigorosa, unindo o conhecimento
                de nutricionistas, médicos e educadores físicos.
              </p>
              <p style={{ lineHeight: 1.9 }}>
                Acreditamos que o gostinho caseiro pode caminhar junto com a ciência nutricional. Por isso,
                nossos produtos são naturais, livres de conservantes e óleos. São opções inclusivas, sem glúten
                e sem lactose, que ficam prontas para o consumo em apenas 6 minutos. Além disso, possuem
                validade de até <strong className="text-cf-yellow">180 dias</strong> quando congelados,
                mantendo todo o frescor original.
              </p>
            </Col>
            <Col lg={6}>
              <ImagePlaceholder height="480px" label="Foto histórica / fundação da empresa" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ===== DESTAQUE "COMIDA DE VERDADE" ===== */}
      <section
        className="py-5"
        style={{
          background: 'var(--cf-yellow)',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <Container className="text-center">
          <h2
            className="fw-black text-uppercase mb-1"
            style={{ fontSize: 'clamp(1.6rem, 4vw, 2.6rem)', color: 'var(--cf-black)', letterSpacing: -0.5 }}
          >
            "Comida de Verdade, Pronta em Minutos!"
          </h2>
          <p className="mb-0 fw-semibold" style={{ color: 'rgba(0,0,0,0.65)', fontSize: '1.1rem' }}>
            Clean Foods Brasil: Otimizando seu tempo, potencializando sua saúde.
          </p>
        </Container>
      </section>

      {/* ===== QUALIDADE & LOGÍSTICA ===== */}
      <section className="section-padding bg-cf-dark">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={6} className="order-lg-2">
              <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                Qualidade e Logística
              </p>
              <h2 className="section-title mb-0">
                Da produção à <span className="highlight">sua mesa</span>
              </h2>
              <hr className="section-divider" />
              <p className="mb-4" style={{ lineHeight: 1.9 }}>
                Nossa produção em larga escala atende com agilidade e precisão uma ampla rede de revendedores.
                Para garantir que cada refeição chegue impecável ao seu destino, contamos com uma{' '}
                <strong className="text-cf-yellow">frota própria de veículos refrigerados</strong>, mantendo
                o controle rigoroso de temperatura e a integridade nutricional de cada ingrediente.
              </p>
              <ul className="list-unstyled">
                {[
                  'Controle rigoroso de temperatura em toda a cadeia',
                  'Produção em larga escala com padrão de qualidade',
                  'Entrega ágil e precisa aos revendedores',
                  'Integridade nutricional preservada',
                ].map((item) => (
                  <li key={item} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.95rem' }}>
                    <FaCheckCircle className="text-cf-yellow flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Col>
            <Col lg={6} className="order-lg-1">
              <ImagePlaceholder height="420px" label="Foto da frota / logística" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ===== NOSSOS VALORES ===== */}
      <section className="section-padding bg-cf-black">
        <Container>
          <div className="text-center mb-5">
            <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
              O que nos move
            </p>
            <h2 className="section-title">
              Nossos <span className="highlight">Valores</span>
            </h2>
            <hr className="section-divider centered mx-auto" />
          </div>
          <Row className="gy-4">
            {VALUES.map(({ Icon, title, text }) => (
              <Col key={title} md={6} lg={4}>
                <div className="cf-card p-4 d-flex gap-3">
                  <div className="cf-card-icon flex-shrink-0">
                    <Icon />
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ fontSize: '1rem' }}>{title}</h5>
                    <p className="mb-0" style={{ fontSize: '0.88rem', lineHeight: 1.7 }}>{text}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ===== BENEFÍCIOS ===== */}
      <section className="section-padding bg-cf-dark">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={6}>
              <p className="text-cf-yellow fw-bold text-uppercase small mb-2" style={{ letterSpacing: 2 }}>
                Benefícios Clean Foods
              </p>
              <h2 className="section-title mb-0">
                Muito além de uma <span className="highlight">marmita fitness</span>
              </h2>
              <hr className="section-divider" />
              <p className="mb-4" style={{ lineHeight: 1.9 }}>
                Nossa linha é indicada para todos os públicos, de crianças a idosos, proporcionando
                uma verdadeira ferramenta de mudança de vida.
              </p>
              <Row className="gy-3">
                {[
                  'Gerenciamento de peso com calorias controladas',
                  'Auxílio no controle do colesterol',
                  'Melhoria da digestão',
                  'Maior estabilidade de energia para treinos',
                  'Indicada de crianças a idosos',
                  'Curadoria científica multidisciplinar',
                ].map((benefit) => (
                  <Col key={benefit} xs={12} sm={6}>
                    <div className="d-flex align-items-start gap-2">
                      <FaCheckCircle className="text-cf-yellow flex-shrink-0 mt-1" />
                      <span style={{ fontSize: '0.9rem' }}>{benefit}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col lg={6}>
              <ImagePlaceholder height="440px" label="Foto atleta / benefícios" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-section">
        <Container>
          <Row className="align-items-center gy-4">
            <Col lg={8}>
              <h2 className="cta-title mb-2">Pronto para fazer parte?</h2>
              <p className="cta-text mb-0">
                Conheça nossos produtos ou seja um revendedor autorizado em sua região.
              </p>
            </Col>
            <Col lg={4} className="d-flex flex-wrap gap-3 justify-content-lg-end">
              <Link to="/nossos-produtos" className="btn btn-lg fw-bold text-uppercase px-4" style={{ background: 'var(--cf-black)', color: 'var(--cf-yellow)', borderRadius: 4 }}>
                Ver Produtos
              </Link>
              <Link to="/seja-revendedor" className="btn btn-lg fw-bold text-uppercase px-4" style={{ background: 'rgba(0,0,0,0.2)', color: 'var(--cf-black)', border: '2px solid var(--cf-black)', borderRadius: 4 }}>
                Seja Revendedor
              </Link>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  )
}
